/**
 * In-app "Find duplicates" detection (Nikki round-3 feedback item #6,
 * explicitly decision-gated on merge — this is READ-ONLY grouping/surfacing,
 * no merge flow, no data-moving mutations).
 *
 * The CSV importer (import-matching.ts) already normalizes email/phone to
 * detect duplicates at import time, but only runs during import — there was
 * previously no way to find existing duplicate contacts after the fact.
 * This module reuses the SAME normalizeEmailForMatch / normalizePhoneForMatch
 * functions so "these look like duplicates" here always agrees with what the
 * importer would have skipped/merged, rather than drifting from a
 * re-derived approximation.
 */

import type { Contact } from "../types";
import { normalizeEmailForMatch, normalizePhoneForMatch } from "./import-matching";

/**
 * Above this bucket size, fuse a shared-field bucket via a linear spanning
 * chain instead of the full O(n²) pairwise mesh. A pathological bucket —
 * hundreds of contacts sharing one normalized email/phone (a broken-import
 * placeholder, or a shared family address that isn't a genuine duplicate set) —
 * would otherwise materialize ~n²/2 actionable edges (a 500-contact bucket =
 * ~125k merge/dismiss rows) and freeze the duplicates page.
 */
const MAX_FULL_MESH_BUCKET = 25;

export interface DuplicateGroup {
  /** Stable React key — sorted, colon-joined contact ids in the group. */
  key: string;
  contacts: Contact[];
  /** Which normalized field(s) tie this group together. */
  matchedBy: Array<"email" | "phone">;
  /**
   * The actual direct-match edges (canonical `[lo, hi]` order, deduped, with
   * dismissed pairs excluded) that formed this group. Unlike `contacts`
   * (which includes everyone transitively pulled into the group), these are
   * the real pairwise matches a "resolve this duplicate" flow should act on.
   */
  matchedPairs: Array<[string, string]>;
}

/**
 * Canonical, order-independent key for a contact pair — matches the DB's
 * least/greatest ordering (see relay.contact_duplicate_resolutions, whose
 * contact_a_id < contact_b_id CHECK enforces the same canonical pair order).
 */
export function dismissedPairKey(a: string, b: string): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

/**
 * Groups contacts that share a normalized email or phone/mobile number.
 *
 * Uses union-find over contact ids so matches are transitive: if A and B
 * share an email, and B and C share a phone number, all three land in one
 * group even though A and C don't directly match each other.
 *
 * `dismissedPairKeys` suppresses the union for that exact pair only — every
 * OTHER pairwise match within a shared-field bucket still unions normally,
 * so dismissing A–B in a 3-way same-email bucket does not orphan B (it still
 * reaches the group via B–C and A–C).
 */
export function groupDuplicateContacts(
  contacts: Contact[],
  dismissedPairKeys: Set<string> = new Set(),
): DuplicateGroup[] {
  const parent = new Map<string, string>();
  const ensure = (id: string) => {
    if (!parent.has(id)) parent.set(id, id);
  };
  const find = (id: string): string => {
    ensure(id);
    let root = id;
    while (parent.get(root) !== root) root = parent.get(root)!;
    // Path compression.
    let cur = id;
    while (parent.get(cur) !== root) {
      const next = parent.get(cur)!;
      parent.set(cur, root);
      cur = next;
    }
    return root;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  for (const c of contacts) ensure(c.id);

  const byEmail = new Map<string, Contact[]>();
  const byPhone = new Map<string, Contact[]>();

  for (const c of contacts) {
    const email = normalizeEmailForMatch(c.email);
    if (email) {
      const arr = byEmail.get(email);
      if (arr) arr.push(c);
      else byEmail.set(email, [c]);
    }

    // A contact's own phone and mobile can normalize to the same digits —
    // dedupe within the contact so it doesn't self-union.
    const seenPhones = new Set<string>();
    for (const raw of [c.phone, c.mobile]) {
      const phone = normalizePhoneForMatch(raw);
      if (phone) seenPhones.add(phone);
    }
    for (const phone of seenPhones) {
      const arr = byPhone.get(phone);
      if (arr) arr.push(c);
      else byPhone.set(phone, [c]);
    }
  }

  // canonical dismissedPairKey -> the actual [lo, hi] edge it represents.
  const edges = new Map<string, [string, string]>();

  // Union EVERY unordered pair within a shared-field bucket (not just
  // chained through bucket[0]) — otherwise dismissing a pair involving the
  // bucket's first member would sever the other members from each other,
  // even though they still directly match one another.
  function fuseBucket(bucket: Contact[]) {
    if (bucket.length > MAX_FULL_MESH_BUCKET) {
      // Oversized bucket: link consecutive members into one component (O(n))
      // and materialize only those consecutive edges as actionable pairs.
      for (let i = 1; i < bucket.length; i++) {
        const a = bucket[i - 1];
        const b = bucket[i];
        // Union unconditionally so the whole bucket stays one group even where
        // a consecutive pair was dismissed (they're still connected via the
        // chain); only skip materializing the dismissed edge itself. This
        // makes the dismissed-pair granularity coarser than the full mesh for
        // these anomalous buckets, an acceptable trade to avoid the freeze.
        union(a.id, b.id);
        const key = dismissedPairKey(a.id, b.id);
        if (dismissedPairKeys.has(key)) continue;
        const lo = a.id < b.id ? a.id : b.id;
        const hi = a.id < b.id ? b.id : a.id;
        edges.set(key, [lo, hi]);
      }
      return;
    }
    for (let i = 0; i < bucket.length; i++) {
      for (let j = i + 1; j < bucket.length; j++) {
        const key = dismissedPairKey(bucket[i].id, bucket[j].id);
        if (dismissedPairKeys.has(key)) continue;
        union(bucket[i].id, bucket[j].id);
        const lo = bucket[i].id < bucket[j].id ? bucket[i].id : bucket[j].id;
        const hi = bucket[i].id < bucket[j].id ? bucket[j].id : bucket[i].id;
        edges.set(key, [lo, hi]);
      }
    }
  }
  for (const group of byEmail.values()) {
    if (group.length > 1) fuseBucket(group);
  }
  for (const group of byPhone.values()) {
    if (group.length > 1) fuseBucket(group);
  }

  const components = new Map<string, Contact[]>();
  for (const c of contacts) {
    const root = find(c.id);
    const arr = components.get(root);
    if (arr) arr.push(c);
    else components.set(root, [c]);
  }

  const result: DuplicateGroup[] = [];
  for (const group of components.values()) {
    if (group.length < 2) continue;

    // Determine matchedBy post-hoc from the final group membership (roots
    // shift during union-find, so re-derive rather than tracking per-union).
    const emailCounts = new Map<string, number>();
    const phoneCounts = new Map<string, number>();
    for (const c of group) {
      const email = normalizeEmailForMatch(c.email);
      if (email) emailCounts.set(email, (emailCounts.get(email) ?? 0) + 1);

      const seenPhones = new Set<string>();
      for (const raw of [c.phone, c.mobile]) {
        const phone = normalizePhoneForMatch(raw);
        if (phone) seenPhones.add(phone);
      }
      for (const phone of seenPhones) {
        phoneCounts.set(phone, (phoneCounts.get(phone) ?? 0) + 1);
      }
    }

    const matchedBy: Array<"email" | "phone"> = [];
    if ([...emailCounts.values()].some((n) => n >= 2)) matchedBy.push("email");
    if ([...phoneCounts.values()].some((n) => n >= 2)) matchedBy.push("phone");

    const sorted = [...group].sort((a, b) => a.created_at.localeCompare(b.created_at));
    const ids = new Set(group.map((c) => c.id));
    const matchedPairs = [...edges.values()].filter(([lo, hi]) => ids.has(lo) && ids.has(hi));
    // A group with no actionable pairs can only arise from the oversized-bucket
    // chain path (which unions members unconditionally): once the user dismisses
    // every displayed chain edge, matchedPairs is empty and the page would show
    // a group with no Merge / Not-a-duplicate controls the user can't clear.
    // Drop it — there's nothing left to resolve. (Normal pairwise groups always
    // carry >=1 edge, so this only affects the fully-dismissed oversized case.)
    if (matchedPairs.length === 0) continue;
    result.push({
      key: sorted.map((c) => c.id).sort().join(":"),
      contacts: sorted,
      matchedBy,
      matchedPairs,
    });
  }

  // Largest groups (most suspected duplicates) surface first.
  result.sort((a, b) => b.contacts.length - a.contacts.length);
  return result;
}
